/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSendExceptionExcelMutation } from "@/services/exception-list";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { toast } from "components/ui/use-toast";
import { X } from "lucide-react";
import { useState } from "react";

interface ModalEditExceptionProps {
  onCloseModal?: () => void;
  onShowFeedback?: (data: {
    successCount: number;
    errorCount: number;
    totalLines: number;
  }) => void;
}

export function ImportDialog({ onCloseModal, onShowFeedback }: ModalEditExceptionProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [resetInput, setResetInput] = useState(0);
  const [sendExceptionExcel, { isLoading: isUploading }] =
    useSendExceptionExcelMutation();

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setResetInput((prev) => prev + 1);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.files && setSelectedFile(e.target.files[0]);
  };

  const handleSendFile = async () => {
    try {
      const formData = new FormData();
      formData.append("files", selectedFile as File);

      await sendExceptionExcel(formData).unwrap();

      toast({
        variant: "default",
        title: "Importação concluída com sucesso!",
        description: "Todas as exceções foram processadas com sucesso.",
        duration: 5000,
      });
      onCloseModal?.();
    } catch (error: any) {
      if (error?.data?.hasErrors) {
        const { totalLines = 0, successCount = 0, errorCount = 0 } = error.data;

        if (errorCount > 0) {
          onCloseModal?.();
          onShowFeedback?.({
            successCount,
            errorCount,
            totalLines,
          });
          return;
        }
      }

      const messages = error?.data?.messages ||
        error?.data?.response?.messages || [
          error?.data?.message || "Erro inesperado ao importar exceções.",
        ];

      toast({
        variant: "destructive",
        title: "Erro na importação",
        description: messages.join("\n"),
        duration: 8000,
      });
      onCloseModal?.();
      console.error("Erro do backend:", error);
    }
  };

  return (
    <div className="w-[600px] h-[400px] rounded-lg shadow-lg bg-white overflow-visible p-10 space-y-6">
      <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">
        Importar Regras de Exceção
      </h2>

      <div className="  mx-auto mb-4 h-[200px] flex flex-col justify-center items-center w-full">
        <Label className="block start mb-5 font-semibold w-full">
          Arquivo de Exceção
        </Label>

        <div className="flex flex-col items-center justify-center border rounded-lg bg-white py-4 w-full">
          <label
            htmlFor="nff-file"
            className="cursor-pointer text-gray-500 text-sm"
          >
            {selectedFile ? selectedFile.name : "Selecione o arquivo"}
          </label>
          <Input
            id="nff-file"
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            className="hidden"
            disabled={isUploading}
            key={`file-${resetInput}`}
          />
        </div>

        <div className="mt-4 w-full">
          {selectedFile && (
            <Card>
              <CardContent className="relative flex justify-center items-center p-4">
                <span className="text-sm truncate text-center">
                  {selectedFile.name}
                </span>
                <X
                  onClick={handleRemoveFile}
                  className="h-5 w-5 cursor-pointer absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-4 items-end mt-auto">
        <Button onClick={onCloseModal} className="bg-transparent w-48">
          Fechar
        </Button>
        <Button
          className="w-48"
          onClick={handleSendFile}
          disabled={!selectedFile}
          isLoading={isUploading}
        >
          Importar
        </Button>
      </div>
    </div>
  );
}